import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteConfirmModal from "../AlertComponents/DeleteConfirmModal";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { BiUpvote } from "react-icons/bi";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import BlockIcon from "@mui/icons-material/Block";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";
import { theme } from "../../theme/colors";
import { canLogPostAction } from "../../utils/accountTypes";
import { getTopicDisplayLabel } from "../../utils/topicLabels";
import { buildBillSlug } from "../../utils/utils";
import { formatPostLocationSingleLine, getPostLocationMapUrl } from "../../utils/postLocationDisplay";
import PostTypeChip from "../CommunityComponents/PostTypeChip";

function formatDate(date) {
  if (!date) return "recently";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function EmptyPostsState({ onCreatePost }) {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        borderRadius: 3,
        p: 4,
        textAlign: "center",
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
        You haven&apos;t posted yet
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: "#64748b" }}>
        Share an action you took, a question you're weighing, or an idea worth discussing.
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 2.5 }}>
        <Button
          variant="contained"
          onClick={onCreatePost}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            background: theme.gradient,
            "&:hover": { background: theme.gradient, opacity: 0.95 },
          }}
        >
          Create your first post
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate("/community")} 
          sx={{ 
            textTransform: "none", 
            fontWeight: 700,
            borderRadius: "10px",
            background: theme.gradient,
            "&:hover": { background: theme.gradient, opacity: 0.95 },
          }}
        >
          Browse Community
        </Button>
      </Stack>
    </Box>
  );
}

function DraftCard({ draft, onEdit, onDelete }) {
  return (
    <Box
      sx={{
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 3,
        p: 2,
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            fontWeight: 800,
            backgroundColor: `${theme.primary}12`,
            color: theme.primary,
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 16 }} />
          Draft
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          Last edited {formatDate(draft.updated_at)}
        </Typography>
      </Stack>

      <Typography
        variant="body1"
        sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
      >
        {draft.content || "Untitled draft..."}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={() => onEdit?.(draft)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            background: theme.gradient,
            "&:hover": { background: theme.gradient, opacity: 0.92 },
          }}
        >
          Continue Editing
        </Button>
        <Tooltip title="Discard">
          <IconButton
            onClick={() => onDelete(draft)}
            aria-label="Discard draft"
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              color: "#475569",
              "&:hover": { backgroundColor: "#f8fafc" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

function PublishedPostCard({
  post,
  currentUserId,
  onToggleLike,
  onDelete,
  onReportPost,
  onBlockAuthor,
  onLogImpact,
  actionLoading,
}) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuOpen = Boolean(menuAnchor);

  const authorId = post.user?.id ?? post.author?.id;
  const isAuthor =
    post.viewer_is_author ??
    (currentUserId != null && authorId != null && String(authorId) === String(currentUserId));

  const viewerHasLiked = post.viewer_has_upvoted || post.viewer_has_liked || post.viewerHasUpvoted;
  const commentCount = post.comment_count ?? post.commentCount ?? 0;
  const upvoteCount = post.upvote_count ?? post.upvoteCount ?? 0;
  const topics = post.topics || post.tags || [];
  const locationLine = formatPostLocationSingleLine(post);

  const contextLabelSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: 0.35,
    display: "block",
  };

  const showImpactButton = canLogPostAction(post);

  const goEdit = () => {
    navigate("/community/create", { state: { editPostId: post.id } });
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #e5e7eb",
        borderRadius: 3,
        p: 2,
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          {formatDate(post.created_at)}
        </Typography>
        <PostTypeChip postType={post.post_type} />
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/community/post/${post.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/community/post/${post.id}`);
          }
        }}
        sx={{ cursor: "pointer", mb: 1.5 }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "#0f172a",
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {post.content}
        </Typography>
        {post.content && post.content.length > 200 && (
          <Typography variant="body2" sx={{ mt: 0.75, color: theme.primary, fontWeight: 600 }}>
            Tap to read more
          </Typography>
        )}
      </Box>

      {locationLine && (
        <Box sx={{ minWidth: 0, mb: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
          <Typography component="span" sx={contextLabelSx}>
            Location
          </Typography>
          <Typography
            component="a"
            href={getPostLocationMapUrl(post) || "#"}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: 14,
              color: "#0f172a",
              lineHeight: 1.45,
              fontWeight: 500,
              textDecoration: "none",
              display: "inline-block",
              "&:hover": { textDecoration: "underline", color: "#0f172a" },
            }}
          >
            {locationLine}
          </Typography>
        </Box>
      )}

      {post.external_link_url && (
        <Box sx={{ mb: 1 }}>
          <Typography component="span" sx={contextLabelSx}>
            External link
          </Typography>
          <Box
            component="a"
            href={post.external_link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", cursor: "pointer", "&:hover": { opacity: 0.8 } }}
          >
            <LinkIcon sx={{ fontSize: 18, color: theme.primary }} />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }}>
              {post.external_link_title || post.external_link_url}
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 14, color: theme.primary }} />
          </Box>
        </Box>
      )}

      {!post.external_link_url && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
          {post.bill && (
            <Box>
              <Typography component="span" sx={contextLabelSx}>
                Link to bill
              </Typography>
              <Box
                component={RouterLink}
                to={`/bills/${buildBillSlug(post.bill)}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", cursor: "pointer", color: theme.primary, "&:hover": { opacity: 0.8 } }}
              >
                <ArticleOutlinedIcon sx={{ fontSize: 18, color: theme.primary }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }} noWrap>
                  {(() => {
                    const t = (post.bill.type || "").toUpperCase().trim();
                    const n = post.bill.bill_number != null ? String(post.bill.bill_number) : "";
                    const label = [t, n].filter(Boolean).join(" ");
                    const snippet = post.bill.title ? post.bill.title.substring(0, 50) + (post.bill.title.length > 50 ? "..." : "") : "";
                    return label ? `${label}${snippet ? " - " + snippet : ""}` : snippet || "View bill";
                  })()}
                </Typography>
              </Box>
            </Box>
          )}

          {post.event && (
            <Box>
              <Typography component="span" sx={contextLabelSx}>
                Link to event
              </Typography>
              <Box
                component={RouterLink}
                to={`/events/${post.event.id}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", cursor: "pointer", color: theme.primary, "&:hover": { opacity: 0.8 } }}
              >
                <EventOutlinedIcon sx={{ fontSize: 18, color: theme.primary }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }}>
                  {post.event.title || "Event"}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
      </Box>

      <Box sx={{ flexShrink: 0, mt: "auto" }}>
        {topics.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 1.25,
            }}
          >
            {topics.slice(0, 3).map((topic, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: "#f3f4f6",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "12px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: 11, fontWeight: 500, color: "#0f172a", lineHeight: 1.3 }}
                >
                  {getTopicDisplayLabel(typeof topic === "object" ? topic?.id ?? topic : topic)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pt: 1.5,
            borderTop: "1px solid #f3f4f6",
          }}
        >
        <Stack direction="row" alignItems="center" spacing={0} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {isAuthor && (
            <Tooltip title="Edit post">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  goEdit();
                }}
                aria-label="Edit post"
                sx={{ color: "#0f172a", mr: 0.5 }}
              >
                <EditOutlinedIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={isAuthor ? "You can't upvote your own post" : viewerHasLiked ? "Remove upvote" : "Upvote"}>
            <span>
              <IconButton
                size="small"
                disabled={isAuthor || actionLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLike(post);
                }}
                aria-label="Upvote"
                sx={{
                  color: isAuthor ? "rgba(100,116,139,0.35)" : viewerHasLiked ? theme.primary : "#64748b",
                  borderRadius: 1,
                }}
              >
                <BiUpvote
                  size={22}
                  color="currentColor"
                  style={{ display: "block" }}
                  aria-hidden
                />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: viewerHasLiked && !isAuthor ? 600 : 400,
              color: isAuthor ? "rgba(100,116,139,0.5)" : viewerHasLiked ? theme.primary : "#64748b",
              minWidth: 16,
              mr: 1,
            }}
          >
            {upvoteCount}
          </Typography>

          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/community/post/${post.id}`);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              py: 0.5,
              px: 0.75,
              ml: 0.5,
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "#64748b",
              fontSize: 14,
              fontFamily: "inherit",
              borderRadius: 1,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
            <span>{commentCount}</span>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {showImpactButton && (
            <Button
              variant="outlined"
              size="small"
              disabled={actionLoading}
              onClick={(e) => {
                e.stopPropagation();
                onLogImpact(post);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 2,
                borderColor: theme.primary,
                color: theme.primary,
                py: 0.5,
                px: 1.5,
                "&:hover": { borderColor: theme.primary, backgroundColor: `${theme.primary}08` },
              }}
            >
              I took action
            </Button>
          )}
          <IconButton
            size="small"
            aria-label="Post menu"
            aria-controls={menuOpen ? `dashboard-post-menu-${post.id}` : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ color: "#64748b" }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id={`dashboard-post-menu-${post.id}`}
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200 } } }}
          >
            {isAuthor ? (
              <>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    goEdit();
                  }}
                  sx={{ gap: 1.5, py: 1.25 }}
                >
                  <EditOutlinedIcon fontSize="small" sx={{ color: "#0f172a" }} />
                  Edit post
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onDelete(post);
                  }}
                  sx={{ gap: 1.5, py: 1.25, color: "#ef4444" }}
                >
                  <DeleteOutlineIcon fontSize="small" sx={{ color: "#ef4444" }} />
                  Delete post
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onReportPost(post);
                  }}
                  sx={{ gap: 1.5, py: 1.25, color: "#ef4444" }}
                >
                  <FlagOutlinedIcon fontSize="small" sx={{ color: "#ef4444" }} />
                  Report post
                </MenuItem>
                {onBlockAuthor && authorId && (
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      onBlockAuthor(post);
                    }}
                    sx={{ gap: 1.5, py: 1.25, color: "#ef4444" }}
                  >
                    <BlockIcon fontSize="small" sx={{ color: "#ef4444" }} />
                    Block user
                  </MenuItem>
                )}
              </>
            )}
          </Menu>
        </Stack>
        </Box>
      </Box>
    </Box>
  );
}

export default function DashboardPostsTab({
  posts,
  drafts,
  currentUserId,
  loading,
  error,
  actionLoading,
  onRetry,
  onToggleLike,
  onDeleteDraft,
  onEditDraft,
  onDeletePost,
  onReportPost,
  onBlockAuthor,
  onLogImpact,
  onCreatePost,
}) {
  const [confirmState, setConfirmState] = useState(null);

  const hasContent = useMemo(() => (posts?.length || 0) > 0 || (drafts?.length || 0) > 0, [posts, drafts]);

  const requestDelete = (item, isDraft = false) => {
    setConfirmState({
      item,
      isDraft,
      title: isDraft ? "Discard Draft" : "Delete Post",
      description: isDraft
        ? "Are you sure you want to discard this draft?"
        : "Are you sure you want to delete this post? This action cannot be undone.",
    });
  };

  const confirmDelete = async () => {
    if (!confirmState) return;
    try {
      if (confirmState.isDraft) {
        await onDeleteDraft(confirmState.item);
      } else {
        await onDeletePost(confirmState.item);
      }
      setConfirmState(null);
    } catch (error) {
      // Error is handled and toasted by the parent component, 
      // but we should still close the modal or let the user try again.
      // We'll close it on success, but leave it open on failure so they can retry or cancel.
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress sx={{ color: theme.primary }} />
        </Box>
      ) : error && !hasContent ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 700 }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : !hasContent ? (
        <EmptyPostsState onCreatePost={onCreatePost} />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            alignContent: "start",
            alignItems: "stretch",
          }}
        >
          {(drafts?.length ?? 0) > 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 1,
                px: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(245, 158, 11, 0.12)",
                border: "1px solid rgba(245, 158, 11, 0.3)",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18, color: "#b45309" }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                You have {drafts.length} unpublished {drafts.length === 1 ? "draft" : "drafts"}
              </Typography>
            </Box>
          )}
          {(drafts || []).map((draft) => (
            <DraftCard
              key={draft.id}
              draft={draft}
              onEdit={onEditDraft}
              onDelete={() => requestDelete(draft, true)}
            />
          ))}
          {(posts || []).map((post) => (
            <PublishedPostCard
              key={post.id}
              post={post}
              currentUserId={currentUserId}
              actionLoading={actionLoading}
              onToggleLike={onToggleLike}
              onDelete={() => requestDelete(post, false)}
              onReportPost={onReportPost}
              onBlockAuthor={onBlockAuthor}
              onLogImpact={onLogImpact}
            />
          ))}
        </Box>
      )}

      <DeleteConfirmModal
        open={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
        onConfirm={confirmDelete}
        title={confirmState?.title}
        description={confirmState?.description}
        confirmLabel={confirmState?.isDraft ? "Discard" : "Delete"}
        loading={actionLoading}
      />
    </Box>
  );
}
